import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiZap, FiWifi } = FiIcons;

/**
 * Shared marketing/branding panel + card shell used by /login and /signup.
 * Mirrors the SaaS dark-slate design language of AppLayout.
 */
const AuthLayout = ({ children, title, subtitle, footer }) => {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0b0f19] flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
        {/* Brand / value panel (hidden on mobile) */}
        <div className="hidden lg:flex flex-col justify-between rounded-2xl bg-slate-900 dark:bg-slate-900/60 border border-slate-800 p-8 text-slate-200">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-xl shadow-lg shadow-blue-900/40">
                🛡️
              </div>
              <div>
                <h2 className="text-base font-black tracking-tight text-white">CertifyPro SaaS</h2>
                <p className="text-[11px] text-slate-400">Multi-Tenant Compliance Platform</p>
              </div>
            </div>

            <h3 className="mt-10 text-2xl font-black leading-snug text-white">
              Statutory certificates for every trade. Offline-first, always compliant.
            </h3>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Gas Safe, NICEIC, OFTEC & MCS accredited certificate generation with secure
              email OTP sign-in, real-time cloud backup and tenant isolation.
            </p>

            <ul className="mt-8 space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-blue-950/60 text-blue-400 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiShield} className="w-4 h-4" />
                </span>
                <div>
                  <p className="text-xs font-bold text-slate-100">Email OTP Authentication</p>
                  <p className="text-[11px] text-slate-400">Passwordless single-use codes — no credentials to leak.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-emerald-950/60 text-emerald-400 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiZap} className="w-4 h-4" />
                </span>
                <div>
                  <p className="text-xs font-bold text-slate-100">Trade-Gated Workspaces</p>
                  <p className="text-[11px] text-slate-400">Engineers only see certificates for their accredited trades.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-amber-950/60 text-amber-400 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiWifi} className="w-4 h-4" />
                </span>
                <div>
                  <p className="text-xs font-bold text-slate-100">Offline-First PWA</p>
                  <p className="text-[11px] text-slate-400">Full site inspections with zero signal. Syncs when back online.</p>
                </div>
              </li>
            </ul>
          </div>

          <p className="text-[10px] text-slate-500 border-t border-slate-800 pt-4">
            UK statutory compliance: Gas Safety (Installations & Use) Regulations 1998 • BS 7671:2018+A2:2022 • OFTEC Standards
          </p>
        </div>

        {/* Auth card */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-6 sm:p-8 flex flex-col">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-lg">
              🛡️
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-900 dark:text-slate-100">CertifyPro SaaS</h2>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">Multi-Tenant Compliance Platform</p>
            </div>
          </div>

          {title && (
            <div className="mb-6">
              <h1 className="text-lg font-black tracking-tight text-slate-900 dark:text-slate-100">{title}</h1>
              {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>}
            </div>
          )}

          <div className="flex-1 flex flex-col">{children}</div>

          {footer && (
            <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 text-center text-xs text-slate-500 dark:text-slate-400">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
